import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';

import 'react-image-gallery/styles/scss/image-gallery.scss';
import 'react-toastify/dist/ReactToastify.css';

import { CssBaseline } from '@mui/material';

import { AppState } from '@auth0/auth0-react';
import { CacheProvider, EmotionCache } from '@emotion/react';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { NextPage } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { AnalyticsContextProvider } from '@arrived/analytics';
import { ApiAuthenticationInterceptor } from '@arrived/api_v2';
import { AuthContextProvider } from '@arrived/auth-web';
import { enUS } from '@arrived/common';
import {
  AppStyles,
  NEW_RELIC_SCRIPT,
  NotificationContainer,
  PromotionHandler,
  SubscribeFooterWrapper,
} from '@arrived/components';
import { ReferralContextProvider } from '@arrived/contexts';
import { FeatureFlagContextProvider } from '@arrived/feature-flags';
import { GoogleTagManagerProvider } from '@arrived/marketing';
import { ThemeProvider } from '@arrived/theme';
import '@arrived/theme/fonts.css';

import { IntercomProvider } from '../components/IntercomProvider';
import { NavMenu } from '../components/navigation/NavMenu';
import { NextAnalytics } from '../components/NextAnalytics';
import { OfferingViewLimitContextProvider } from '../components/OfferingViewLimitContext';
import { SoldOutPopup } from '../components/SoldOutPopup';

import { useDeviceType } from '../hooks/useDeviceType';
import { queryClientConfig } from '../query-client';
import createEmotionCache from '../util/createCache';

import type { AppProps } from 'next/app';

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

type NextPageWithLayout = NextPage & {
  hasNav?: boolean;
  showNotification?: boolean;
  isPropertyDetailsPage?: boolean;
};

type AppPropsWithLayout = AppProps<any> & {
  Component: NextPageWithLayout;
};

interface MyAppProps extends AppPropsWithLayout {
  emotionCache?: EmotionCache;
}

function MyApp({ Component, pageProps, emotionCache = clientSideEmotionCache }: MyAppProps) {
  const deviceType = useDeviceType();
  const router = useRouter();
  const [queryClient] = useState(() => new QueryClient(queryClientConfig));
  const hasNav = Component.hasNav ?? true;

  const [language, setLanguage] = useState('en-US');

  const onRedirectCallback = (appState?: AppState) => {
    router.push(appState?.returnTo || '/');
  };

  useEffect(() => {
    if (navigator?.language) {
      setLanguage(navigator.language);
    }
  }, []);

  return (
    <CacheProvider value={emotionCache}>
      <Head>
        <title>Arrived Homes | Easily invest in rental properties | Real Estate Investing Platform</title>
        <link rel="icon" type="image/x-icon" href="/arrived.ico" />
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      {/* Can't be located in Head https://github.com/vercel/next.js/issues/27498 */}
      <Script id="new-relic" strategy="beforeInteractive">
        {NEW_RELIC_SCRIPT}
      </Script>
      <IntlProvider locale={language} defaultLocale="en-US" messages={enUS}>
        <ThemeProvider deviceType={deviceType}>
          {AppStyles}
          <CssBaseline />
          <AnalyticsContextProvider>
            <NextAnalytics>
              <QueryClientProvider client={queryClient}>
                <GoogleTagManagerProvider>
                  <Hydrate state={pageProps.dehydratedState}>
                    <AuthContextProvider allowUnauthorized onRedirectCallback={onRedirectCallback}>
                      <ApiAuthenticationInterceptor>
                        <FeatureFlagContextProvider>
                          <ReferralContextProvider>
                            <PromotionHandler>
                              <IntercomProvider>
                                <SubscribeFooterWrapper>
                                  <OfferingViewLimitContextProvider>
                                    <SoldOutPopup>
                                      {hasNav ? (
                                        <NavMenu
                                          isPropertyDetailsPage={Component.isPropertyDetailsPage}
                                          showNotificationBanner={Component.showNotification ?? false}
                                        >
                                          {<Component {...pageProps} />}
                                        </NavMenu>
                                      ) : (
                                        <Component {...pageProps} />
                                      )}
                                      <ReactQueryDevtools />
                                    </SoldOutPopup>
                                  </OfferingViewLimitContextProvider>
                                </SubscribeFooterWrapper>
                              </IntercomProvider>
                            </PromotionHandler>
                          </ReferralContextProvider>
                        </FeatureFlagContextProvider>
                      </ApiAuthenticationInterceptor>
                    </AuthContextProvider>
                  </Hydrate>
                </GoogleTagManagerProvider>
              </QueryClientProvider>
            </NextAnalytics>
          </AnalyticsContextProvider>
          <NotificationContainer />
        </ThemeProvider>
      </IntlProvider>
    </CacheProvider>
  );
}

export default MyApp;
